import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Activity, CheckCircle2, AlertTriangle, XCircle, RefreshCw } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OVERALL = {
  operational: { label: "All Systems Operational", cls: "border-emerald-500/50 bg-emerald-500/10 text-emerald-300", Icon: CheckCircle2 },
  degraded: { label: "Degraded Performance", cls: "border-amber-500/50 bg-amber-500/10 text-amber-300", Icon: AlertTriangle },
  major_outage: { label: "Major Outage", cls: "border-rose-500/50 bg-rose-500/10 text-rose-300", Icon: XCircle },
};
const SEV = { minor: "text-amber-300", major: "text-orange-300", critical: "text-rose-300" };

export default function SystemStatus() {
  const [sys, setSys] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([axios.get(`${API}/status/system`), axios.get(`${API}/status/incidents`)])
      .then(([a, b]) => { setSys(a.data); setIncidents(b.data.incidents); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, []);

  const ov = OVERALL[sys?.overall] || OVERALL.operational;

  return (
    <div className="min-h-screen bg-[#0a0c12] text-white" data-testid="system-status-page">
      <div className="max-w-3xl mx-auto px-5 py-10">
        <div className="flex items-center justify-between mb-8">
          <Link to="/" className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-[#f59e0b]" />
            <span className="font-bold tracking-tight">BroadcastFlow AI <span className="text-[#5f6779] font-normal">Status</span></span>
          </Link>
          <button type="button" onClick={load} data-testid="status-refresh-btn"
            className="inline-flex items-center gap-1.5 rounded-md border border-[#262b3a] px-3 py-1.5 text-xs text-[#9aa3b5] hover:text-white transition">
            <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>

        {sys && (
          <div className={`rounded-xl border p-5 flex items-center gap-3 mb-8 ${ov.cls}`} data-testid="status-overall-banner">
            <ov.Icon className="h-6 w-6" />
            <div>
              <div className="font-bold">{ov.label}</div>
              <div className="text-xs opacity-70">Checked {new Date(sys.server_time).toLocaleString()} · auto-refreshes every minute</div>
            </div>
          </div>
        )}

        <div className="space-y-2 mb-10" data-testid="status-components">
          {(sys?.components || []).map((c) => (
            <div key={c.key} className="flex items-center justify-between rounded-lg border border-[#262b3a] bg-[#12151f] px-4 py-3"
              data-testid={`status-component-${c.key}`}>
              <div>
                <div className="text-sm font-medium">{c.name}</div>
                <div className="text-[11px] text-[#5f6779]">{c.detail}</div>
              </div>
              <span className={`inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider ${c.ok ? "text-emerald-300" : "text-rose-300"}`}>
                <span className={`h-2 w-2 rounded-full ${c.ok ? "bg-emerald-400" : "bg-rose-400"}`} />
                {c.ok ? "Operational" : "Down"}
              </span>
            </div>
          ))}
        </div>

        <h2 className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#5f6779] mb-3">// Incident History</h2>
        <div className="space-y-3" data-testid="status-incidents">
          {incidents.length === 0 && (
            <div className="rounded-lg border border-[#262b3a] bg-[#12151f] px-4 py-6 text-center text-sm text-[#5f6779]">
              No incidents reported. Smooth broadcasting.
            </div>
          )}
          {incidents.map((inc) => (
            <div key={inc.incident_id} className="rounded-lg border border-[#262b3a] bg-[#12151f] p-4" data-testid={`incident-${inc.incident_id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold">{inc.title}</span>
                <span className={`font-mono text-[9px] uppercase tracking-wider ${SEV[inc.severity] || ""}`}>{inc.severity}</span>
                <span className={`ml-auto font-mono text-[9px] uppercase tracking-wider ${inc.status === "resolved" ? "text-emerald-300" : "text-amber-300"}`}>
                  {inc.status}
                </span>
              </div>
              <div className="mt-2 space-y-1.5">
                {(inc.updates || []).slice().reverse().map((u, i) => (
                  <div key={i} className="text-xs text-[#9aa3b5]">
                    <span className="font-mono text-[9px] text-[#5f6779] mr-2">{new Date(u.at).toLocaleString()}</span>
                    <span className="uppercase text-[9px] font-mono mr-2 text-[#f59e0b]">{u.status}</span>
                    {u.message}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 text-center text-[10px] font-mono uppercase tracking-[0.2em] text-[#3a4152]">
          BroadcastFlow AI · <Link to="/dashboard/help" className="hover:text-[#9aa3b5]">Help Center</Link>
        </div>
      </div>
    </div>
  );
}
